import { useMemo } from 'react'
import { useCreateFieldsetCompletion } from 'hooks/queries/workspaces/useCreateFieldsetCompletion'
import { MultiStepSchema, WizardType } from '../schemas/Types'
import { AIJobStatus, FieldsetCompletion } from 'types'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { FormStatus } from '../Wizard/Form/Types'
import { FormikErrors } from 'formik'
import { useCompletedFieldsForRequest } from 'components/Wizards/Wizard/Form/useCompletedFieldsForRequest'

interface Props {
  schema: MultiStepSchema<WizardType>
  onLoadingChange?: (value: boolean) => void
  setStatus?: (status?: any) => void
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<unknown>>
  initialStatus?: any
  values: {
    [key: string]: any
  }
}

export const useAutocomplete = ({
  onLoadingChange,
  schema,
  values,
  setStatus,
  initialStatus,
  setFieldValue,
}: Props) => {
  const { mutate, data } = useCreateFieldsetCompletion()

  const completedFields = useMemo(() => {
    const valueEntries = Object.entries(values)
    const nonEmptyValues = valueEntries.filter(([_key, value]) => {
      if (Array.isArray(value)) {
        return value.length !== 0
      }
      return value !== '' && value !== null
    })
    return Object.fromEntries(nonEmptyValues)
  }, [values])

  const completedFieldsForRequest = useCompletedFieldsForRequest(completedFields, values)

  const autoCompleteFields = async (fields: FieldsetCompletion['completed_fields']) => {
    if (Object.keys(fields).length > 0) {
      const promises: Array<Promise<void | FormikErrors<any>>> = []
      Object.entries(fields).forEach(([key, value]) => {
        promises.push(setFieldValue(key, value, true))
      })
      return Promise.all(promises)
    }
    return Promise.resolve()
  }

  const handleComplete = async (fieldsetCompletion: FieldsetCompletion) => {
    if (fieldsetCompletion.status === AIJobStatus.Failed) {
      showErrorMessageToast('Blaze was unable to complete the request. Please contact support if this issue persists.')
      setStatus?.(initialStatus)
      onLoadingChange?.(false)

      return
    }

    try {
      await autoCompleteFields(fieldsetCompletion.completed_fields)
      setStatus?.(FormStatus.AutoCompleted)
    } catch {
      setStatus?.(initialStatus)
    } finally {
      onLoadingChange?.(false)
    }
  }

  const handleGenerate = ({
    fields,
    newCompletedFields,
  }: {
    fields: string[]
    newCompletedFields?: {
      [field: string]: any
    }
  }) => {
    onLoadingChange?.(true)

    // filter out completed fields that are in the fields array
    // so that we don't use that context when generating the new fields
    const filteredCompletedFields = Object.fromEntries(
      Object.entries(completedFieldsForRequest).filter(([key]) => !fields.includes(key)),
    )
    mutate(
      {
        fields,
        completedFields: { ...filteredCompletedFields, ...newCompletedFields },
        generationType: schema.meta.channel,
      },
      {
        onError: () => {
          setStatus?.(initialStatus)
          onLoadingChange?.(false)
        },
      },
    )
  }

  return {
    handleGenerate,
    handleComplete,
    data,
  }
}
